import { Component } from '@angular/core';
import {NgFor, NgIf} from '@angular/common';
import { RouterLink } from '@angular/router';

type Offer = {
  title: string;
  subtitle: string;
  price: string;
  features: string[];
  result: string;
  cta: string;
  tone: 'green' | 'blue' | 'violet';
  recommended?: boolean;
  icon: string;
};

@Component({
  selector: 'app-pricing',
  standalone: true,
  imports: [NgFor, RouterLink, NgIf],
  template: `
  <section class="pricing" id="pricing">
    <div class="pricing__bg" aria-hidden="true"></div>

    <div class="container">
      <div class="head">
        <h2 class="title">Nos offres <span>Data & IA</span></h2>
        <p class="subtitle">De la structuration à la prédiction.</p>
      </div>

      <div class="grid">
        <article class="offer"
                 *ngFor="let o of offers"
                 [class.green]="o.tone === 'green'"
                 [class.blue]="o.tone === 'blue'"
                 [class.violet]="o.tone === 'violet'"
                 [class.recommended]="o.recommended">

          <div class="offer__icon">{{ o.icon }}</div>

          <div class="recommendedBadge" *ngIf="o.recommended">
            <span class="star">★</span>
            <span>Recommandé</span>
          </div>

          <div class="offer__inner">
            <h3 class="offer__title">{{ o.title }}</h3>
            <p class="offer__subtitle">{{ o.subtitle }}</p>

            <div class="offer__price">{{ o.price }}</div>

            <ul class="offer__list">
              <li *ngFor="let f of o.features">{{ f }}</li>
            </ul>

            <div class="offer__resultLabel">Résultat :</div>
            <p class="offer__result">{{ o.result }}</p>

            <a class="offer__cta"
               [class.primary]="o.recommended"
               routerLink="/contact">
              {{ o.cta }}
              <span class="arrow">→</span>
            </a>
          </div>
        </article>
      </div>
    </div>
  </section>
  `,
  styles: [`
    .pricing{
      position: relative;
      overflow: hidden;
      padding: 54px 0 64px;
      border-radius: 28px;
      border: 1px solid rgba(255,255,255,.08);
      background:
        radial-gradient(1000px 520px at 15% 90%, rgba(0,229,168,.08), transparent 60%),
        radial-gradient(1100px 580px at 85% 10%, rgba(79,125,255,.14), transparent 60%),
        rgba(255,255,255,.02);
      box-shadow: 0 30px 120px rgba(0,0,0,.40);
    }

    .pricing__bg{
      position: absolute;
      inset: -20%;
      pointer-events: none;
      opacity: .9;
      background:
        radial-gradient(circle at 18% 70%, rgba(0,229,168,.16), transparent 28%),
        radial-gradient(circle at 52% 34%, rgba(79,125,255,.18), transparent 30%),
        radial-gradient(circle at 82% 36%, rgba(168,85,247,.18), transparent 30%),
        repeating-linear-gradient(120deg,
          rgba(255,255,255,.04) 0 2px,
          transparent 2px 44px);
      filter: blur(8px);
      animation: prismDrift 14s linear infinite;
    }

    .head{
      position: relative;
      text-align: center;
      margin-bottom: 18px;
      z-index: 1;
    }

    .title{
      margin: 0;
      font-size: clamp(32px, 4.6vw, 58px);
      line-height: 1.05;
      font-weight: 1000;
      letter-spacing: -.8px;
      color: rgba(255,255,255,.96);
    }

    .title span{
      background: linear-gradient(135deg, #bfffe8, #9bdcff);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
    }

    .subtitle{
      margin: 10px 0 0;
      font-size: clamp(16px, 2vw, 22px);
      color: rgba(235,240,255,.82);
      font-weight: 700;
    }

    .grid{
      position: relative;
      z-index: 1;
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      padding-top: 42px;
      gap: 18px;
      align-items: stretch;
    }

    .offer{
      position: relative;
      border-radius: 28px;
      border: 1px solid rgba(255,255,255,.14);
      background: rgba(8,12,24,.55);
      overflow: visible;
      box-shadow: 0 24px 90px rgba(0,0,0,.34);
      min-height: 100%;
    }

    .offer:hover{
      transform: translateY(-6px);
      box-shadow: 0 34px 110px rgba(0,0,0,.40);
      border-color: rgba(255,255,255,.22);
    }

    .offer::before{
      content:"";
      position:absolute;
      inset: 0;
      border-radius: 28px;
      pointer-events:none;
      opacity: .95;
    }

    .offer.green::before{
      background:
        radial-gradient(circle at 18% 12%, rgba(0,229,168,.20), transparent 28%),
        radial-gradient(circle at 50% 100%, rgba(166,255,90,.08), transparent 30%);
      box-shadow: inset 0 0 0 1px rgba(106,255,196,.16);
    }

    .offer.blue::before{
      background:
        radial-gradient(circle at 18% 12%, rgba(79,125,255,.22), transparent 28%),
        radial-gradient(circle at 72% 0%, rgba(0,229,255,.10), transparent 26%);
      box-shadow: inset 0 0 0 1px rgba(120,170,255,.18);
    }

    .offer.violet::before{
      background:
        radial-gradient(circle at 18% 12%, rgba(168,85,247,.22), transparent 28%),
        radial-gradient(circle at 72% 0%, rgba(255,61,206,.08), transparent 26%);
      box-shadow: inset 0 0 0 1px rgba(194,132,255,.18);
    }

    .offer__icon{
      position: absolute;
      top: -28px;
      left: 50%;
      transform: translateX(-50%);
      width: 78px;
      height: 78px;
      display: grid;
      place-items: center;
      border-radius: 999px;
      font-size: 38px;
      border: 1px solid rgba(255,255,255,.18);
      background: rgba(11,16,34,.88);
      box-shadow: 0 20px 60px rgba(0,0,0,.35);
      z-index: 3;
      animation: iconLift 5.2s ease-in-out infinite;
    }

    .green .offer__icon{
      box-shadow:
        0 20px 60px rgba(0,0,0,.35),
        0 0 30px rgba(0,229,168,.35);
    }

    .blue .offer__icon{
      box-shadow:
        0 20px 60px rgba(0,0,0,.35),
        0 0 30px rgba(79,125,255,.35);
    }

    .violet .offer__icon{
      box-shadow:
        0 20px 60px rgba(0,0,0,.35),
        0 0 30px rgba(168,85,247,.35);
    }

    .recommendedBadge{
      position: absolute;
      top: -46px;
      left: 50%;
      transform: translateX(-50%);
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 10px 18px;
      border-radius: 999px;
      border: 1px solid rgba(255,215,90,.24);
      background: rgba(21,24,36,.95);
      color: rgba(255,255,255,.95);
      font-weight: 1000;
      font-size: 14px;
      box-shadow: 0 16px 40px rgba(0,0,0,.35);
      z-index: 4;
      white-space: nowrap;
    }

    .star{
      color: #ffd24a;
      font-size: 18px;
      line-height: 1;
    }

    .offer__inner{
      position: relative;
      z-index: 2;
      padding: 88px 24px 24px;
      display: flex;
      flex-direction: column;
      min-height: 100%;
    }

    .offer:hover{
      transform: translateY(-6px);
      box-shadow: 0 34px 110px rgba(0,0,0,.40);
      border-color: rgba(255,255,255,.22);
    }

    .offer__title{
      margin: 0;
      font-size: clamp(24px, 2.2vw, 34px);
      line-height: 1.12;
      font-weight: 1000;
      letter-spacing: -.4px;
      color: rgba(255,255,255,.96);
    }

    .offer__subtitle{
      margin: 10px 0 0;
      color: rgba(235,240,255,.82);
      font-size: 15px;
      line-height: 1.5;
      font-weight: 700;
      min-height: 52px;
    }

    .offer__price{
      margin: 22px 0 18px;
      font-size: clamp(28px, 3vw, 50px);
      line-height: 1;
      font-weight: 1000;
      letter-spacing: -.8px;
      color: rgba(255,255,255,.98);
      padding-top: 16px;
      border-top: 1px solid rgba(255,255,255,.08);
    }

    .offer__list{
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: 10px;
    }

    .offer__list li{
      position: relative;
      padding-left: 28px;
      color: rgba(235,240,255,.88);
      font-size: 15px;
      line-height: 1.45;
      font-weight: 700;
    }

    .offer__list li::before{
      content: "✓";
      position: absolute;
      left: 0;
      top: 0;
      font-weight: 1000;
    }

    .green .offer__list li::before{ color: #9cf5c0; }
    .blue .offer__list li::before{ color: #86b7ff; }
    .violet .offer__list li::before{ color: #c9a6ff; }

    .offer__resultLabel{
      margin-top: 22px;
      font-size: 18px;
      font-weight: 1000;
      color: rgba(255,255,255,.92);
      padding-top: 16px;
      border-top: 1px solid rgba(255,255,255,.08);
    }

    .green .offer__resultLabel{ color: #bfffe0; }
    .blue .offer__resultLabel{ color: #a7c8ff; }
    .violet .offer__resultLabel{ color: #d5b4ff; }

    @keyframes softOfferFloat{
      0%,100%{ transform: translateY(0); }
      50%{ transform: translateY(-6px); }
    }

    @keyframes iconLift{
      0%,100%{ transform: translateX(-50%) translateY(0); }
      50%{ transform: translateX(-50%) translateY(-5px); }
    }

    @keyframes prismDrift{
      0%{ transform: translate3d(-1%, 0, 0) scale(1); }
      50%{ transform: translate3d(1%, -1%, 0) scale(1.03); }
      100%{ transform: translate3d(-1%, 0, 0) scale(1); }
    }

    .offer__result{
      margin: 8px 0 22px;
      color: rgba(235,240,255,.84);
      font-size: 15px;
      line-height: 1.5;
      font-weight: 700;
    }

    .offer__cta{
      margin-top: auto;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      min-height: 54px;
      padding: 0 22px;
      border-radius: 999px;
      text-decoration: none;
      font-weight: 1000;
      font-size: 16px;
      border: 1px solid rgba(255,255,255,.14);
      background: rgba(255,255,255,.06);
      color: rgba(255,255,255,.96);
      box-shadow: inset 0 1px 0 rgba(255,255,255,.06);
      transition: transform .14s ease, filter .14s ease, border-color .14s ease;
      align-self: flex-start;
    }

    .offer__cta:hover{
      transform: translateY(-1px);
      filter: brightness(1.04);
    }

    .offer__cta.primary{
      background: linear-gradient(135deg, rgba(79,125,255,.95), rgba(0,229,168,.88));
      color: #061024;
      border-color: rgba(255,255,255,.08);
    }

    .green .offer__cta{
      box-shadow: 0 12px 34px rgba(0,229,168,.12);
    }

    .blue .offer__cta{
      box-shadow: 0 12px 34px rgba(79,125,255,.14);
    }

    .violet .offer__cta{
      box-shadow: 0 12px 34px rgba(168,85,247,.14);
    }

    .arrow{
      font-size: 18px;
      line-height: 1;
    }

    @media (max-width: 1100px){
      .grid{
        grid-template-columns: 1fr;
        gap: 54px;
        padding-top: 38px;
      }

      .offer__inner{
        padding-top: 82px;
      }

      .offer__subtitle{
        min-height: unset;
      }
    }

    @media (max-width: 560px){
      .pricing{
        padding: 44px 0 44px;
        border-radius: 22px;
      }

      .recommendedBadge{
        top: -40px;
        font-size: 13px;
        padding: 8px 14px;
      }

      .offer__icon{
        width: 68px;
        height: 68px;
        top: -24px;
        font-size: 32px;
      }

      .offer__inner{
        padding: 76px 16px 16px;
      }

      .offer__cta{
        width: 100%;
      }
    }

    :host-context(body[data-theme="light"]) .pricing{
      background:
        radial-gradient(900px 480px at 10% 88%, rgba(0,229,168,.08), transparent 60%),
        radial-gradient(980px 520px at 84% 6%, rgba(79,125,255,.12), transparent 60%),
        linear-gradient(180deg, rgba(255,255,255,.86), rgba(248,250,252,.92));
      border-color: rgba(15,23,42,.08);
      box-shadow: 0 24px 70px rgba(15,23,42,.08);
    }

    :host-context(body[data-theme="light"]) .title,
    :host-context(body[data-theme="light"]) .subtitle,
    :host-context(body[data-theme="light"]) .offer__title,
    :host-context(body[data-theme="light"]) .offer__subtitle,
    :host-context(body[data-theme="light"]) .offer__price,
    :host-context(body[data-theme="light"]) .offer__list,
    :host-context(body[data-theme="light"]) .offer__result,
    :host-context(body[data-theme="light"]) .offer__resultLabel{
      color: #0f172a;
    }

    :host-context(body[data-theme="light"]) .offer{
      background: linear-gradient(180deg, rgba(255,255,255,.96), rgba(248,250,252,.92));
      border-color: rgba(15,23,42,.08);
      box-shadow: 0 18px 40px rgba(15,23,42,.08);
    }

    :host-context(body[data-theme="light"]) .offer__cta{
      background: rgba(255,255,255,.92);
      color: #0f172a;
      border-color: rgba(15,23,42,.10);
    }

  `]
})
export class PricingComponent {
  offers: Offer[] = [
    {
      title: 'Data Foundation',
      subtitle: 'Idéal pour structurer vos bases data',
      price: '€ À partir de 3 500 €',
      features: [
        'Audit data & KPI',
        'Analyse qualité des données',
        'Cartographie des sources',
        'Roadmap priorisée',
        '1 dashboard exécutif'
      ],
      result: 'Une vision claire de votre maturité data et des quick wins activables immédiatement.',
      cta: 'Démarrer',
      tone: 'green',
      icon: '🛢️'
    },
    {
      title: 'Performance Analytics',
      subtitle: 'Pour piloter votre performance avec des dashboards fiables',
      price: '€ À partir de 8 500 €',
      features: [
        'Modèle data optimisé',
        '3 à 5 dashboards décisionnels',
        'Gouvernance KPI',
        'Automatisation & refresh',
        'Alerting intelligent'
      ],
      result: 'Des décisions pilotées par des KPI fiables et partagés.',
      cta: 'Lancer le projet',
      tone: 'blue',
      icon: '⭐',
      recommended: true
    },
    {
      title: 'Data Science & IA',
      subtitle: 'Pour passer du reporting à la prédiction',
      price: 'Sur devis',
      features: [
        'Forecasting & scoring',
        'Modèles prédictifs',
        'Machine Learning explicable',
        'Détection d’anomalies',
        'Monitoring & amélioration continue'
      ],
      result: 'Anticipation, optimisation et gain de performance mesurable.',
      cta: 'Parler à un expert',
      tone: 'violet',
      icon: '🧠'
    }
  ];
}
